import { CometChat } from '@cometchat/chat-sdk-javascript';
import { CometChatGroups, CometChatUsers } from '@cometchat/chat-uikit-react';
import { useEffect, useMemo, useRef, useState, type MouseEvent } from 'react';
import { useCalls, type CallKind } from './calls/calls-context';
import { Dialog } from './components/Dialog/Dialog';
import { MdIcon } from './components/Icon/MdIcon';
import { CALL_GROUP_TAG } from './group-tags';
import './ListScreen.css';

/**
 * The Groups tab: the groups this user can ring (plan W3.4; mirrors
 * Android's GroupsFragment + NewGroupActivity).
 *
 * Lists only joined groups tagged CALL_GROUP_TAG, so the chat group each
 * meeting creates never shows here (those open from History). Each row has
 * voice / video buttons that ring the whole group through the ringing flow.
 * "New group call" picks users, creates a PRIVATE tagged group, adds them,
 * and rings it.
 */

interface RowGroup {
  getGuid(): string;
  getName(): string;
  getIcon(): string;
}

function GroupCallButtons({ group }: { group: RowGroup }) {
  const { startCall, busy } = useCalls();
  const name = group.getName() || group.getGuid();
  const call = (type: CallKind) => (e: MouseEvent) => {
    e.stopPropagation();
    startCall(
      { uid: group.getGuid(), name, avatar: group.getIcon() || undefined, kind: 'group' },
      type,
    );
  };
  return (
    <span className="user-call-buttons">
      <button
        type="button"
        className="md-icon-button"
        onClick={call('audio')}
        disabled={busy}
        aria-label={`Voice call ${name}`}
        title="Voice call">
        <MdIcon name="call" size={20} />
      </button>
      <button
        type="button"
        className="md-icon-button"
        onClick={call('video')}
        disabled={busy}
        aria-label={`Video call ${name}`}
        title="Video call">
        <MdIcon name="videocam" size={20} />
      </button>
    </span>
  );
}

function randomGuid(): string {
  return `grp-${crypto.randomUUID().replace(/-/g, '').slice(0, 16)}`;
}

/** Pick the members and start the call; the group is named after them. */
function NewGroupCallDialog({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (group: CometChat.Group, type: CallKind) => void;
}) {
  const [search, setSearch] = useState('');
  const searchRef = useRef<HTMLInputElement>(null);
  // After the dialog's own focus move (to its close button), which runs in
  // the parent's effect after this one: the search is where typing starts.
  useEffect(() => {
    const t = setTimeout(() => searchRef.current?.focus(), 0);
    return () => clearTimeout(t);
  }, []);
  const [selected, setSelected] = useState<Map<string, CometChat.User>>(new Map());
  const [working, setWorking] = useState<CallKind | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Selection is kept here and toggled on row click, with our own tick in
  // the trailing slot. The UIKit's selectionMode renders every row's
  // checkbox with the same id and its own checked state, and its onSelect
  // didn't fire reliably from a click.
  const toggle = (user: CometChat.User) => {
    setSelected(prev => {
      const next = new Map(prev);
      if (next.has(user.getUid())) next.delete(user.getUid());
      else next.set(user.getUid(), user);
      return next;
    });
  };

  const create = async (type: CallKind) => {
    if (selected.size === 0 || working) return;
    setWorking(type);
    setError(null);
    try {
      const members = [...selected.values()];
      const names = members.map(u => u.getName());
      const groupName =
        names.slice(0, 3).join(', ') + (names.length > 3 ? ` +${names.length - 3}` : '');
      const group = new CometChat.Group(
        randomGuid(),
        groupName,
        CometChat.GROUP_TYPE.PRIVATE,
        undefined,
      );
      group.setTags([CALL_GROUP_TAG]);
      // Two calls, not createGroupWithMembers: that one sends the tags as a
      // string and the backend drops them (same finding as Android).
      const created = await CometChat.createGroup(group);
      await CometChat.addMembersToGroup(
        created.getGuid(),
        members.map(
          u => new CometChat.GroupMember(u.getUid(), CometChat.GROUP_MEMBER_SCOPE.PARTICIPANT),
        ),
        [],
      );
      onCreated(created, type);
    } catch (e) {
      console.error('Creating the group failed:', e);
      setError("Couldn't create the group. Try again.");
      setWorking(null);
    }
  };

  const count = selected.size;
  return (
    <Dialog title="New group call" onClose={onClose}>
      <div className="group-new">
        <label className="group-new__label" htmlFor="group-search">
          Members{count > 0 ? ` · ${count} selected` : ''}
        </label>
        <input
          id="group-search"
          type="search"
          className="form-input group-new__search"
          placeholder="Search people"
          value={search}
          onChange={e => setSearch(e.target.value)}
          autoComplete="off"
          ref={searchRef}
        />
        <div className="group-new__picker">
          <CometChatUsers
            hideSearch
            searchKeyword={search}
            onItemClick={toggle}
            trailingView={user => (
              <span
                className={`group-new__tick${selected.has(user.getUid()) ? ' is-on' : ''}`}
                aria-hidden="true">
                <MdIcon
                  name={selected.has(user.getUid()) ? 'check_box' : 'check_box_outline_blank'}
                  size={22}
                />
              </span>
            )}
          />
        </div>
        {error && (
          <p className="group-new__error" role="alert">
            {error}
          </p>
        )}
        <div className="group-new__actions">
          <button
            type="button"
            className="secondary-btn"
            disabled={count === 0 || working !== null}
            onClick={() => create('audio')}>
            <MdIcon name="call" size={18} />
            {working === 'audio' ? 'Starting…' : 'Start voice call'}
          </button>
          <button
            type="button"
            className="primary-btn active"
            disabled={count === 0 || working !== null}
            onClick={() => create('video')}>
            <MdIcon name="videocam" size={18} />
            {working === 'video' ? 'Starting…' : 'Start video call'}
          </button>
        </div>
      </div>
    </Dialog>
  );
}

function Groups() {
  const { startCall, busy } = useCalls();
  const [showNew, setShowNew] = useState(false);
  // A new key remounts the list so a just-created group shows up.
  const [listKey, setListKey] = useState(0);

  const groupsRequestBuilder = useMemo(
    () =>
      new CometChat.GroupsRequestBuilder()
        .setLimit(30)
        .joinedOnly(true)
        .setTags([CALL_GROUP_TAG]),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [listKey],
  );

  return (
    <div className="list-screen">
      <div className="list-screen__toolbar">
        <button
          type="button"
          className="md-tonal-button"
          onClick={() => setShowNew(true)}
          disabled={busy}>
          <MdIcon name="group_add" size={18} />
          New group call
        </button>
      </div>
      <div className="list-screen__body">
        <CometChatGroups
          key={listKey}
          groupsRequestBuilder={groupsRequestBuilder}
          trailingView={group => <GroupCallButtons group={group} />}
        />
      </div>
      {showNew && (
        <NewGroupCallDialog
          onClose={() => setShowNew(false)}
          onCreated={(group, type) => {
            setShowNew(false);
            setListKey(k => k + 1);
            startCall(
              { uid: group.getGuid(), name: group.getName(), kind: 'group' },
              type,
            );
          }}
        />
      )}
    </div>
  );
}

export default Groups;
